import { useEffect, useRef, useState } from "react";
import {
  getTrail,
  uploadTrailBanner,
  deleteTrailBanner,
  ApiError,
} from "../api";

/**
 * Trail banner uploader — Facebook-style header image.
 *
 * Drop or pick → center-crop to 16:9 → resize to 1600×900 JPEG @ 80%
 * → upload to R2 via the worker. Simpler than the org-logo uploader
 * since banners don't need an interactive crop: the user provides a
 * wide enough photo and we center-fit it. Trade-off: a portrait shot
 * gets cropped tight; the empty-state guidance steers them toward
 * landscape.
 */

const OUTPUT_WIDTH = 1600;
const OUTPUT_HEIGHT = 900;
const MAX_RAW_BYTES = 12 * 1024 * 1024;
const ACCEPTED = "image/png,image/jpeg,image/webp";

export function BannerUploader({ slug }: { slug: string }) {
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [percent, setPercent] = useState(0);
  const [phase, setPhase] = useState<"idle" | "cooking" | "uploading" | "done">("idle");
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function reload() {
    setLoading(true);
    try {
      const r = await getTrail(slug);
      setCurrentUrl(r.trail.banner?.url ?? null);
    } catch {
      setCurrentUrl(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  function pick() {
    fileInputRef.current?.click();
  }

  async function processAndUpload(file: File) {
    setError(null);
    if (!file.type.startsWith("image/")) {
      setError("That doesn't look like an image. JPEG, PNG, or WebP please.");
      return;
    }
    if (file.size > MAX_RAW_BYTES) {
      setError("File is over 12 MB. Pick something smaller.");
      return;
    }
    setUploading(true);
    setPercent(0);
    setPhase("cooking");
    try {
      const blob = await renderBannerBlob(file, OUTPUT_WIDTH, OUTPUT_HEIGHT);
      setPhase("uploading");
      const r = await uploadTrailBanner(slug, blob, OUTPUT_WIDTH, OUTPUT_HEIGHT, (p) =>
        setPercent(p)
      );
      setCurrentUrl(r.banner.url);
      setPhase("done");
      setTimeout(() => {
        setPhase("idle");
        setUploading(false);
        setPercent(0);
      }, 900);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed.");
      setPhase("idle");
      setUploading(false);
      setPercent(0);
    }
  }

  function onFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) processAndUpload(file);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processAndUpload(file);
  }

  async function handleDelete() {
    if (!confirm("Remove the header banner? Riders will see the default header until you upload a new one.")) return;
    try {
      await deleteTrailBanner(slug);
      setCurrentUrl(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't remove the banner.");
    }
  }

  return (
    <div className="admin-section">
      <div className="admin-section-heading">Header banner</div>
      <div className="admin-section-body">
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          Show off your trail with a hero photo at the top of every rider's
          detail screen. Wide landscape shots work best — we'll center-crop
          and resize to {OUTPUT_WIDTH}×{OUTPUT_HEIGHT}.
        </p>

        {loading ? (
          <div className="muted" style={{ fontSize: 13, marginTop: 10 }}>Loading…</div>
        ) : currentUrl ? (
          <div className="banner-preview-wrap">
            <img className="banner-preview" src={currentUrl} alt="Current trail banner" />
            <div className="row" style={{ gap: 8, marginTop: 10 }}>
              <button type="button" className="small secondary" onClick={pick} disabled={uploading}>
                Replace
              </button>
              <button type="button" className="small ghost" onClick={handleDelete} disabled={uploading}>
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`banner-dropzone ${dragOver ? "is-over" : ""}`}
            onClick={pick}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            <div className="banner-dropzone-copy">
              <strong>Drop a photo here</strong>
              <span className="muted" style={{ fontSize: 12 }}>
                or click to pick from your computer
              </span>
            </div>
          </div>
        )}

        {uploading && (
          <div className="banner-progress">
            <div
              className="banner-progress-fill"
              style={{ width: `${Math.max(4, percent)}%` }}
            />
            <span className="banner-progress-label">
              {phase === "cooking"
                ? "Cooking it up…"
                : phase === "uploading"
                  ? `Uploading… ${percent}%`
                  : phase === "done"
                    ? "Looks epic ✨"
                    : ""}
            </span>
          </div>
        )}

        {error && <div className="admin-feedback admin-feedback-err">{error}</div>}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          onChange={onFileInput}
          style={{ display: "none" }}
        />
      </div>
    </div>
  );
}

/**
 * Center-crop the source image to 16:9 and resize to outputW × outputH,
 * exported as JPEG @ 0.8. Keeps the focal weight in the middle which
 * is the safe bet for trail photos (rider, sign, vista).
 */
async function renderBannerBlob(
  file: File,
  outputW: number,
  outputH: number
): Promise<Blob> {
  const image = await loadImage(URL.createObjectURL(file));
  const targetAspect = outputW / outputH;
  const srcAspect = image.width / image.height;

  let sx = 0, sy = 0, sw = image.width, sh = image.height;
  if (srcAspect > targetAspect) {
    // Source is wider than target → crop sides.
    sw = image.height * targetAspect;
    sx = (image.width - sw) / 2;
  } else if (srcAspect < targetAspect) {
    // Source is taller than target → crop top + bottom.
    sh = image.width / targetAspect;
    sy = (image.height - sh) / 2;
  }

  const canvas = document.createElement("canvas");
  canvas.width = outputW;
  canvas.height = outputH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, outputW, outputH);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Failed to encode the image"));
        resolve(blob);
      },
      "image/jpeg",
      0.8
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Couldn't decode the image"));
    img.src = src;
  });
}
